import React from 'react';
import { Button } from 'antd';
// Types
import { AnyTask, ExampleFile } from 'helpers/customCampaign';
import { GetCampaignsV2_getCampaigns_entities_activities_CustomBriefActivity_tasksV2_CampaignAppearanceTask } from 'api/campaignV2/types/GetCampaignsV2';
// Helpers
import { APPEARANCE_TYPE_OPTIONS } from 'helpers/customCampaignConstants';
import { getPostDueDateLabel } from 'helpers/timelineDeliverables';
import {
  getUgcContentDueDateLabel,
  getUgcContentPiecesLabel,
} from 'helpers/campaignUgc';
import { getCategory } from 'helpers/customCampaign';
// Components
import UploadFilesInput from 'ui/UploadFIlesInput/UploadFilesInput';
// Icons
import {
  EditOutlined,
  DeleteOutlined,
  CameraOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
// Styles
import styles from './CustomDeliverableItem.module.scss';

type CustomDeliverableItemProps = {
  deliverable: AnyTask;
  onEdit: () => void;
  onRemove: () => void;
  onExamplesChange: (examples: ExampleFile[]) => void;
};

const APPEARANCE_TYPE_LABEL = (value?: string | null): string =>
  APPEARANCE_TYPE_OPTIONS.find((opt) => opt.value === value)?.label ??
  (value || 'Appearance');

const CustomDeliverableItem = ({
  deliverable,
  onEdit,
  onRemove,
  onExamplesChange,
}: CustomDeliverableItemProps): JSX.Element => {
  const category = getCategory(deliverable);
  const isAppearance = category === 'Appearance';
  const isUgc = category === 'UGC';

  const appearance =
    isAppearance &&
    (deliverable as unknown as GetCampaignsV2_getCampaigns_entities_activities_CustomBriefActivity_tasksV2_CampaignAppearanceTask);

  return (
    <div className={styles.item}>
      <div className={styles.itemMain}>
        <div className={styles.label}>{category.toUpperCase()}</div>
        <h3 className={styles.itemTitle}>{deliverable.title || category}</h3>

        <div className={styles.itemInfo}>
          {isAppearance && appearance ? (
            <>
              <div className={styles.infoRow}>
                <EnvironmentOutlined />
                <span className={styles.infoLabel}>Type:</span>
                <span>{APPEARANCE_TYPE_LABEL(appearance.appearanceType)}</span>
              </div>
              {appearance.location && (
                <div className={styles.infoRow}>
                  <EnvironmentOutlined />
                  <span className={styles.infoLabel}>Location:</span>
                  <span>{appearance.location}</span>
                </div>
              )}
              {appearance.durationText && (
                <div className={styles.infoRow}>
                  <CalendarOutlined />
                  <span className={styles.infoLabel}>Duration:</span>
                  <span>{appearance.durationText}</span>
                </div>
              )}
            </>
          ) : (
            <>
              <div className={styles.infoRow}>
                <CameraOutlined />
                <span className={styles.infoLabel}>Content pieces:</span>
                <span>
                  {isUgc
                    ? getUgcContentPiecesLabel(deliverable)
                    : `${deliverable.contentItems?.length ?? 0} ${
                        deliverable.contentItems?.length === 1
                          ? 'piece'
                          : 'pieces'
                      } of content`}
                </span>
              </div>
              <div className={styles.infoRow}>
                <CalendarOutlined />
                <span className={styles.infoLabel}>
                  {isUgc ? 'Content' : 'Post'} Due Date:
                </span>
                <span>
                  {isUgc
                    ? getUgcContentDueDateLabel(deliverable)
                    : getPostDueDateLabel(deliverable)}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      <div className={styles.itemActions}>
        <Button
          type="text"
          size="small"
          icon={<EditOutlined />}
          onClick={(e) => {
            e.preventDefault();
            onEdit();
          }}
        >
          Edit
        </Button>
        <Button
          type="text"
          size="small"
          danger
          icon={<DeleteOutlined />}
          onClick={(e) => {
            e.preventDefault();
            onRemove();
          }}
        >
          Remove
        </Button>
      </div>

      {isUgc && (
        <div className={styles.examplesUploader}>
          <UploadFilesInput
            title="Deliverable Examples & Guidelines"
            value={(deliverable as any).examples || []}
            onChange={onExamplesChange}
            acceptFileTypes={[
              'image/png',
              'image/jpeg',
              'video/mp4',
              'video/quicktime',
              'application/pdf',
            ]}
            inputDescription="Upload brand guidelines, scripts, or examples for this deliverable."
          />
        </div>
      )}
    </div>
  );
};

export default CustomDeliverableItem;
